import { formatInTimeZone } from 'date-fns-tz';
import { useAtomValue } from 'jotai';
import {
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    CircleAlertIcon,
    Icon,
    ThumbsDownIcon,
    ThumbsUpIcon,
    TileButton,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { formatDollars } from '../../../common/utils/bankingUtils';
import { getCadenceDisplayString } from '../../../common/utils/billUtils';
import type { AccountSummary } from '../../../holistic/types/HolisticSummary';
import { billsAccountsAtom, selectedBillAtom } from '../../state/BillsAtoms';
import {
    type _Bill,
    AiToPostgresBillingCadence,
    EditProposedChangesRequestAction,
} from '../../types/Bill';

interface ProposedChangesTransactionDetailsProps {
    bill: Required<_Bill> | Partial<_Bill>;
}

const ProposedChangesTransactionDetails = ({
    bill,
}: ProposedChangesTransactionDetailsProps) => {
    const billAccounts = useAtomValue(billsAccountsAtom);

    const { accountId: billAccountId, payee, proposedChanges } = bill;
    const { accountId: proposedAccountId, latestTransactionId } =
        proposedChanges ?? {};

    const account = billAccounts?.find(
        (a) => a.id === (proposedAccountId ?? billAccountId),
    );
    const { name, accountNumberMasked } = account ?? {};

    const transaction = bill.transactions?.find(
        (t) => t.transactionId === latestTransactionId,
    );
    const { transactionDate, transactionAmount } = transaction ?? {};

    return (
        <XStack
            paddingHorizontal={20}
            paddingVertical={16}
            borderRadius={12}
            borderWidth={1}
            borderColor="$gray200"
            alignItems="center"
            justifyContent="space-between"
        >
            <YStack gap={4}>
                <BodyMediumEmphasized>{payee}</BodyMediumEmphasized>
                <BodySmallRegular color="$gray1000">
                    {name != null
                        ? `${name} *${accountNumberMasked}`
                        : 'Unknown account'}
                </BodySmallRegular>
            </YStack>
            <YStack gap={4}>
                <BodyMediumEmphasized textAlign="right">
                    {formatDollars(transactionAmount)}
                </BodyMediumEmphasized>
                <BodySmallRegular textAlign="right" color="$gray1000">
                    {transactionDate != null
                        ? formatInTimeZone(
                              new Date(transactionDate),
                              'UTC',
                              'MMM d, yyyy',
                          )
                        : 'Unknown date'}
                </BodySmallRegular>
            </YStack>
        </XStack>
    );
};

interface ProposedChangesAttributesRowProps {
    label: string;
    value: string;
}

const ProposedChangesAttributesRow = ({
    label,
    value,
}: ProposedChangesAttributesRowProps) => {
    return (
        <XStack alignItems="flex-start" gap={4}>
            <View>
                <Icon icon={CircleAlertIcon} />
            </View>
            <BodyMediumRegular>
                {label}{' '}
                <BodyMediumRegular color="$orange1000">
                    {value}
                </BodyMediumRegular>
            </BodyMediumRegular>
        </XStack>
    );
};

interface ProposedChangesAttributesProps {
    bill: Required<_Bill> | Partial<_Bill>;
}

const ProposedChangesAttributes = ({
    bill,
}: ProposedChangesAttributesProps) => {
    const billAccounts = useAtomValue(billsAccountsAtom);

    const { accountId, averageAmount, frequency } = bill.proposedChanges ?? {};
    const proposedAccount =
        accountId != null
            ? (billAccounts?.find((a) => a.id === accountId) as AccountSummary)
            : null;

    return (
        <YStack gap={6}>
            {averageAmount != null && (
                <ProposedChangesAttributesRow
                    label={`Amount ${averageAmount < (bill.amount ?? 0) ? 'decreased' : 'increased'} to`}
                    value={formatDollars(averageAmount)}
                />
            )}
            {frequency != null && (
                <ProposedChangesAttributesRow
                    label="Frequency changed to"
                    value={getCadenceDisplayString(
                        AiToPostgresBillingCadence[frequency],
                    )}
                />
            )}
            {proposedAccount && (
                <ProposedChangesAttributesRow
                    label="Account changed to"
                    value={`*${proposedAccount.accountNumberMasked}`}
                />
            )}
        </YStack>
    );
};

interface ProposedChangesButtonsProps {
    onPress: (editAction) => void;
}

const ProposedChangesButtons = ({ onPress }: ProposedChangesButtonsProps) => {
    return (
        <XStack gap={12} height={66}>
            <TileButton
                tileProps={{ backgroundColor: '$green200' }}
                onPress={() => {
                    onPress(EditProposedChangesRequestAction.APPROVE);
                }}
                icon={ThumbsUpIcon}
                color="$green1100"
                text="Looks correct"
            />
            <TileButton
                tileProps={{ backgroundColor: '$red200' }}
                onPress={() => {
                    onPress(EditProposedChangesRequestAction.REJECT);
                }}
                icon={ThumbsDownIcon}
                color="$red1100"
                text="Looks wrong"
            />
        </XStack>
    );
};

interface BillDetailsProposedChangesProps {
    onActionPress: (action: EditProposedChangesRequestAction) => void;
}

const BillDetailsProposedChanges = ({
    onActionPress,
}: BillDetailsProposedChangesProps) => {
    const bill = useAtomValue(selectedBillAtom);
    if (!bill?.proposedChanges) return null;

    return (
        <YStack gap={20} paddingBottom={20}>
            <BodyLargeEmphasized>
                We detected changes for you to review based on this transaction
            </BodyLargeEmphasized>

            <ProposedChangesTransactionDetails bill={bill} />
            <ProposedChangesAttributes bill={bill} />
            <ProposedChangesButtons onPress={onActionPress} />
        </YStack>
    );
};

export default BillDetailsProposedChanges;
